import { AfterViewInit, Component, ElementRef, inject, OnDestroy, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import * as mapboxgl from 'mapbox-gl';
import { environment } from '../../environments/environment';
import { ensureMapboxWorker } from './mapbox-worker';
import * as turf from '@turf/turf';
import type { Feature, FeatureCollection, Point } from 'geojson';
import { TRACES_AMBER } from '../company/manage/map/traces-finding-layer';
import { OverlayState } from '../company/manage/map/forest-layers';
import { LayersPanelComponent } from '../company/manage/map/layers-panel.component';
import { MapElementControl } from '../company/manage/map/map-element-control';
import { riskPartsToLineFeatures } from '../company/manage/map/whisp-layer';
import { MapOverlaysController } from '../company/manage/map/map-overlays.controller';
import { parseFeatureCollection } from '../company/manage/map/map-traces-geo.controller';
import { MapFeatureListController, siteListFeatures } from '../company/manage/map/map-feature-list.controller';
import { whispFeatureLookup } from '../company/manage/map/whisp-feature-findings';
import type { AppId, WhispSiteView } from '../model';
import { AppAuthService } from './auth/app-auth.service';
import { WhispDebugService } from '../company/manage/map/whisp-debug.service';
import { WhispDebugPanelComponent } from '../company/manage/map/whisp-debug-panel.component';
import { pointAreaLabel } from '../company/manage/map/map-point.models';

class CloseDialogControl implements mapboxgl.IControl {
  private _container!: HTMLElement;

  constructor(
    private onClick: () => void,
    private title = 'Close'
  ) {}

  onAdd(map: mapboxgl.Map): HTMLElement {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'mapboxgl-ctrl-icon';
    btn.setAttribute('aria-label', this.title);
    btn.title = this.title;
    btn.style.marginBottom = '-3px';
    btn.innerHTML = `
      <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
        <path d="M18.3 5.71a1 1 0 0 0-1.41 0L12 10.59 7.11 5.7A1 1 0 0 0 5.7 7.11L10.59 12l-4.9 4.89a1 1 0 1 0 1.41 1.41L12 13.41l4.89 4.9a1 1 0 0 0 1.41-1.41L13.41 12l4.9-4.89a1 1 0 0 0-.01-1.4Z"/>
      </svg>
    `;
    btn.onclick = this.onClick;

    this._container = document.createElement('div');
    this._container.className = 'mapboxgl-ctrl-group mapboxgl-ctrl';
    this._container.style.zIndex = '9999'; // ensure above
    this._container.appendChild(btn);
    return this._container;
  }

  onRemove(): void {
    this._container?.remove();
  }

  getDefaultPosition(): mapboxgl.ControlPosition {
    return 'top-right';
  }
}

export interface MapViewDialogData {
  geoJson: string;
  /** Circle markers, e.g. a TRACES rejection's `RejectionItem.point` (app#287 Task 8). */
  markers?: [number, number][];
  /** 1-based `RejectionItem.place` indices — outlined as an amber polygon line. */
  highlightPlaces?: number[];
  whisp?: WhispSiteView | null;
  fitBbox?: number[];
  whispGroupId?: number;
  tracesGeoJson?: string | null;
  siteId?: AppId;
}

@Component({
  selector: 'app-map-view-dialog',
  imports: [MatDialogModule, MatToolbarModule, MatButtonModule, MatIconModule, TranslateModule, LayersPanelComponent, WhispDebugPanelComponent],
  template: `
    <mat-dialog-content class="map-dialog-content" #mapContainer></mat-dialog-content>
    <div class="control-parking">
      <app-layers-panel
        [state]="overlays"
        [showWhisp]="hasRiskOutline"
        [showWhispDebug]="showWhispDebug"
        [whispStale]="whispStale"
        (stateChange)="applyOverlays($event)"
      />
      <app-whisp-debug-panel
        [view]="overlays.whispDebug ? whispDebug.view() : null"
        [siteGeo]="siteGeo"
        [selectedPartId]="whispDebug.selectedPartId()"
        (selectPart)="whispDebug.select($event)"
      />
    </div>
  `,
  styleUrl: './map-view-dialog.component.scss',
  styles: [
    `
      .map-dialog-content {
        height: 100vh;
        padding: 0;
        max-height: 100vh;
        font-size: 10px;
      }
      .control-parking {
        display: none;
      }
    `,
  ],
})
export class MapViewDialogComponent implements AfterViewInit, OnDestroy {
  data = inject<MapViewDialogData>(MAT_DIALOG_DATA);
  private dialogRef = inject(MatDialogRef<MapViewDialogComponent>, { optional: true });
  @ViewChild('mapContainer', { static: true }) mapContainer!: ElementRef<HTMLDivElement>;
  @ViewChild(LayersPanelComponent, { static: true, read: ElementRef }) layersPanel!: ElementRef<HTMLElement>;
  @ViewChild(WhispDebugPanelComponent, { static: true, read: ElementRef }) whispDebugPanel!: ElementRef<HTMLElement>;

  map?: mapboxgl.Map;
  private readonly translate = inject(TranslateService);
  private readonly mapOverlays = new MapOverlaysController(() => this.map, inject(WhispDebugService));
  readonly whispDebug = this.mapOverlays.whispDebug;
  readonly showWhispDebug = inject(AppAuthService).isAdmin && !!this.data.siteId;
  readonly siteGeo = parseFeatureCollection(this.data.geoJson) ?? null;

  get whispStale(): boolean {
    return this.whispDebug.view()?.current === false;
  }

  get hasRiskOutline(): boolean {
    return !!this.data.whisp?.riskParts?.length;
  }

  get overlays(): OverlayState {
    return this.mapOverlays.state;
  }

  applyOverlays(state: OverlayState) {
    this.mapOverlays.apply(state);
  }

  ngAfterViewInit() {
    try {
      ensureMapboxWorker();
      this.map = new mapboxgl.Map({
        accessToken: environment.mapbox.accessToken,
        container: this.mapContainer.nativeElement,
        style: 'mapbox://styles/mapbox/satellite-streets-v12',
        zoom: 2,
        center: [0, 20],
        cooperativeGestures: false,
        dragRotate: true,
        dragPan: true,
      });

      this.map.on('load', () => {
        if (!this.map) return;

        try {
          this.map.resize();
          this.map.addControl(new CloseDialogControl(() => this.dialogRef?.close()), 'top-right');
          this.map.addControl(new mapboxgl.NavigationControl());
          this.map.addControl(new MapElementControl(this.layersPanel.nativeElement), 'bottom-right');
          this.mapOverlays.attachForest();
          if (this.data.geoJson) {
            this.addGeoJsonToMap(this.data.geoJson);
            this.addFeatureList();
          }
          this.addTracesFindingsToMap();
          this.addWhispRiskOutline();
          this.mapOverlays.showTracesGeometry(this.data.tracesGeoJson);
          this.attachWhispDebug();
          this.fitToRequestedBox();
        } catch (err) {
          console.error('Failed to load map', err);
        }
      });
    } catch (err) {
      console.error('Failed to initialize map', err);
    }
    setTimeout(() => this.map?.resize(), 100);
  }

  ngOnDestroy() {
    this.map?.remove();
    this.map = undefined;
  }

  private fitToRequestedBox() {
    const [west, south, east, north] = this.data.fitBbox ?? [];
    if (!this.map || north === undefined) return;
    this.map.fitBounds(
      [
        [west, south],
        [east, north],
      ],
      { padding: 50, maxZoom: 15 }
    );
  }

  private addFeatureList() {
    if (!this.map) return;
    const site = parseFeatureCollection(this.data.geoJson);
    new MapFeatureListController(this.map, siteListFeatures(site), this.translate, {
      whisp: whispFeatureLookup(site, this.data.whisp),
      selectGroupId: this.data.whispGroupId,
    }).attach();
  }

  private attachWhispDebug() {
    if (!this.map || !this.showWhispDebug || !this.data.siteId) return;
    this.map.addControl(new MapElementControl(this.whispDebugPanel.nativeElement), 'top-right');
    void this.whispDebug.load(this.data.siteId);
  }

  private addWhispRiskOutline() {
    this.mapOverlays.attachWhispRisk();
    this.mapOverlays.showWhispRisk(
      riskPartsToLineFeatures(parseFeatureCollection(this.data.geoJson), this.data.whisp?.riskParts)
    );
  }

  private addGeoJsonToMap(geoJsonStr: string) {
    if (!this.map) return;
    const data = withPointAreaLabels(JSON.parse(geoJsonStr));

    this.map.addSource('geojson-data', { type: 'geojson', data });
    this.map.addLayer({
      id: 'geojson-fill',
      type: 'fill',
      source: 'geojson-data',
      paint: { 'fill-color': '#00C853', 'fill-opacity': 0.3 },
    });

    this.map.addLayer({
      id: 'geojson-outline',
      type: 'line',
      source: 'geojson-data',
      paint: { 'line-color': '#00C853', 'line-width': 2 },
    });

    this.map.addLayer({
      id: 'geojson-points',
      type: 'circle',
      source: 'geojson-data',
      filter: ['==', ['geometry-type'], 'Point'],
      paint: { 'circle-radius': 7, 'circle-color': '#7c3aed', 'circle-stroke-width': 2, 'circle-stroke-color': '#fff' },
    });
    if (this.map.getStyle()?.glyphs) {
      this.map.addLayer({
        id: 'geojson-point-areas',
        type: 'symbol',
        source: 'geojson-data',
        filter: ['==', ['geometry-type'], 'Point'],
        layout: { 'text-field': ['get', 'areaLabel'], 'text-size': 12, 'text-offset': [0, 1.1], 'text-anchor': 'top' },
        paint: { 'text-color': '#7c3aed', 'text-halo-color': '#fff', 'text-halo-width': 2 },
      });
    }

    const bbox = turf.bbox(data);
    this.map.fitBounds(
      [
        [bbox[0], bbox[1]],
        [bbox[2], bbox[3]],
      ],
      { padding: 50, maxZoom: 15 }
    );
  }

  /**
   * Amber TRACES markers on the read-only map (app#287 Task 8) — `circle`/`line` layers only,
   * same amber as the map editor's `err-traces-*` layers. `highlightPlaces` is 1-based
   * (`RejectionItem.place`), resolved against the SAME `geoJson` feature collection just added.
   */
  private addTracesFindingsToMap() {
    if (!this.map) return;
    const markers = this.data.markers ?? [];
    const highlightPlaces = this.data.highlightPlaces ?? [];
    if (!markers.length && !highlightPlaces.length) return;

    const pointFeatures: Feature<Point>[] = markers.map((m) => ({
      type: 'Feature',
      properties: {},
      geometry: { type: 'Point', coordinates: m },
    }));

    let outlineFeatures: Feature[] = [];
    if (highlightPlaces.length && this.data.geoJson) {
      try {
        const geo = JSON.parse(this.data.geoJson) as { features?: Feature[] };
        const features = geo.features ?? [];
        outlineFeatures = highlightPlaces
          .map((place) => features[place - 1])
          .filter((f): f is Feature => !!f?.geometry)
          .map((f) => ({ type: 'Feature', properties: {}, geometry: f.geometry }));
      } catch {
        // malformed geoJson — markers still render below
      }
    }

    this.map.addSource('traces-finding-data', {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [...outlineFeatures, ...pointFeatures] } as any,
    });
    this.map.addLayer({
      id: 'traces-finding-line',
      type: 'line',
      source: 'traces-finding-data',
      filter: ['==', ['geometry-type'], 'Polygon'],
      paint: { 'line-color': TRACES_AMBER, 'line-width': 3 },
    });
    this.map.addLayer({
      id: 'traces-finding-point',
      type: 'circle',
      source: 'traces-finding-data',
      filter: ['==', ['geometry-type'], 'Point'],
      paint: {
        'circle-radius': 8,
        'circle-color': TRACES_AMBER,
        'circle-stroke-width': 2,
        'circle-stroke-color': '#fff',
      },
    });

    if (pointFeatures.length) {
      this.map.flyTo({ center: pointFeatures[0].geometry.coordinates as [number, number], zoom: 15 });
    } else if (outlineFeatures.length) {
      const bbox = turf.bbox({ type: 'FeatureCollection', features: outlineFeatures } as any);
      this.map.fitBounds(
        [
          [bbox[0], bbox[1]],
          [bbox[2], bbox[3]],
        ],
        { padding: 80, maxZoom: 15 }
      );
    }
  }
}

function withPointAreaLabels(data: FeatureCollection): FeatureCollection {
  return {
    ...data,
    features: (data.features ?? []).map((feature) =>
      feature.geometry?.type === 'Point'
        ? { ...feature, properties: { ...feature.properties, areaLabel: pointAreaLabel(feature.properties) } }
        : feature
    ),
  };
}
